// The ADD-ALARM PICKER (`.context/alarms-redesign/spec-all.md`, "Add-alarm
// picker — PHASE 3"): the panel `+ ADD ALARM` opens above the list, which asks
// the one question an owner actually has — WHAT SHOULD THE BOAT WATCH? — and
// answers it from the boat's own signal catalog rather than from a library of
// recipes they have to translate into their own equipment.
//
// WHAT IT CREATES. A CUSTOM alarm, through `lib/customAlarmForm` — the same
// draft, the same `validate`, the same `toCreateBody` the full builder uses, so
// this panel can never author something the builder would refuse (or refuse
// something it would accept). Ready-made recipe alarms are a different thing
// with different gating (`minCoreVersion`, instance pins, duplicate rules) and
// they keep their own page: the quiet "Browse ready-made alarms" link at the
// foot goes there. Half a recipe picker in a panel would be worse than a link.
//
// THE CREATE IS NOT OPTIMISTIC. `POST /v1/boats/:boatId/alerts` with a fresh
// `Idempotency-Key`; on 201 the panel refreshes the page's alarm snapshot
// (`onCreated`) and shows a RECEIPT rather than navigating away — most owners
// add two or three alarms in one sitting, and a panel that jumps to a detail
// page after each one makes that three round trips. The receipt's words are
// `proposalCreatedMessage(compileStatus)`: `pending` is a SUCCESS that is NOT
// yet watching anything, and it must never be narrated as "watching".
//
// WHAT IT DELIBERATELY DOES NOT DRAW:
//
//   - No mini-bar in the rows. The design puts a 110×10 bar beside each value;
//     the catalog carries no min/max scale for an arbitrary signal, and a bar
//     drawn against an invented 0..100 would be a picture of nothing. The value
//     and its freshness carry the row (data-mapping: NOW cell has no bounds).
//   - No prefilled SET POINT. The design shows "drops below [11.8]"; nothing on
//     this boat says what 11.8 should be, and a threshold nobody chose being
//     armed on their behalf is the one failure mode worth avoiding here. The
//     field starts empty and CREATE stays disabled, naming what is missing.
//   - No inline condition builder. "+ but only while…" is a link into the full
//     custom builder, which owns the signal picker every clause needs.
//
// LIVE VALUES come from `useBoatChrome().liveSamples` resolved against the
// catalog by `lib/signalLive.signalReading` — the live frame wins on TIME, which
// is the same rule the row list behind this panel reads its values with, so the
// two can never print different "now"s for one signal.

import type { VesselType } from '@neomaris/protocol';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../../api';
import type { AlarmSeverity } from '../../../lib/alarmDisplay';
import {
  DURATION_UNIT,
  segmentsPlainText,
  sentenceInputFromDraft,
  sentenceSegments,
  isInertWatchPreset,
  watchSlotOptions,
  severityOptions,
  type SentenceSegment,
} from '../../../lib/alarmInlineEditor';
import {
  autoTitleFor,
  createBlocker,
  draftForSignal,
  firstCategory,
  pickerCategories,
  pickerRow,
  signalsInGroup,
  withAutoTitle,
} from '../../../lib/alarmPicker';
import { errorContextFor, mapCreateError } from '../../../lib/customAlarmErrors';
import {
  customValueUnits,
  setClearDelaySeconds,
  setClearpoint,
  setSetDelaySeconds,
  setSetpoint,
  setSeverity,
  setStaleSeconds,
  setEnabledModes,
  toCreateBody,
  type CustomAlarmDraft,
} from '../../../lib/customAlarmForm';
import { proposalCreatedMessage } from '../../../lib/neoAlarmProposal';
import { signalDisplayName, type Signal, type SignalGroup } from '../../../lib/signalCatalog';
import { useUnits } from '../../../lib/useUnits';
import { watchPresetModes, type WatchPresetId } from '../../../lib/watchSchedule';
import { useBoatChrome } from '../../app/boatChrome';
import { CheckCircleFillIcon, ChevronDownIcon, CloseIcon } from '../../app/icons';

/**
 * The unit chip beside this draft's value boxes: the ACCOUNT's where a
 * conversion applies, the signal's own declared unit otherwise. One resolver,
 * the same one that filled the box, so the label and the number can never come
 * from two places.
 */
function unitForBox(draft: CustomAlarmDraft, signal: Signal): string | null {
  const box = customValueUnits(draft);
  return box.converted ? box.unitSuffix : signal.unit;
}

/** The create response fields this panel reads (same shape as the builder's). */
interface CreateResponse {
  readonly id: string;
  readonly compileStatus: string | null;
}

export interface AddAlarmPickerProps {
  boatId: string;
  /** The boat's signal catalog by path — the picker's whole universe. */
  catalogByPath: ReadonlyMap<string, Signal>;
  /** How many definitions already watch each path ("2 alarms exist"). */
  alarmCounts: ReadonlyMap<string, number>;
  /** Mirrored vessel type — it changes the watch preset WORDS, never the modes. */
  vesselType: VesselType | null;
  /** The page clock, so freshness wording never disagrees with the rows below. */
  now: number;
  /** Close the panel, changing nothing. */
  onClose: () => void;
  /** A create landed: re-read the alarm snapshot so the list shows the new row. */
  onCreated: () => void;
}

/** The design's dropdown slot — a real `<select>` wearing the chip. */
function SlotSelect<V extends string>({
  value,
  options,
  onChange,
  label,
  testId,
}: {
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
  label: string;
  testId: string;
}) {
  return (
    <span className="al-slot">
      <select
        className="al-slot__select"
        value={value}
        aria-label={label}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value as V)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="al-slot__caret" />
    </span>
  );
}

/** The design's number field, with the BOX's unit beside it in muted type —
 *  the account's where a conversion applies, the boat's otherwise. */
function NumberSlot({
  value,
  onChange,
  unit,
  label,
  testId,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  unit?: string | null;
  label: string;
  testId: string;
  placeholder?: string;
}) {
  return (
    <span className="al-numslot">
      <input
        className="al-numslot__input"
        type="text"
        inputMode="decimal"
        value={value}
        aria-label={label}
        data-testid={testId}
        {...(placeholder !== undefined ? { placeholder } : {})}
        onChange={(e) => onChange(e.target.value)}
      />
      {unit !== null && unit !== undefined && unit.trim().length > 0 ? (
        <span className="al-unit">{unit}</span>
      ) : null}
    </span>
  );
}

export function AddAlarmPicker({
  boatId,
  catalogByPath,
  alarmCounts,
  vesselType,
  now,
  onClose,
  onCreated,
}: AddAlarmPickerProps) {
  // The live lane, taken from the shared boat chrome rather than threaded down
  // as a prop: this panel is the only thing on the screen that needs a reading
  // for a signal NO alarm watches yet, and the page's own value lines are built
  // for definitions. Same provider, same map, same resolution rule.
  const { liveSamples } = useBoatChrome();
  // The account's display units (ADR 0189). They ride the draft this panel
  // creates, so the set-point box below reads and writes in the owner's unit —
  // and the same object restates every row's live reading, so the list and the
  // box agree.
  const units = useUnits();

  const signals = useMemo(() => [...catalogByPath.values()], [catalogByPath]);
  const categories = useMemo(() => pickerCategories(signals), [signals]);
  const [group, setGroup] = useState<SignalGroup | null>(null);
  // The chosen category, or the first one the boat has. A category can DISAPPEAR
  // between catalog reads (its last signal went away), and a rail with nothing
  // selected showing an empty list would read as a broken panel — so a choice
  // that no longer exists falls back rather than sticking.
  const selectedGroup =
    group !== null && categories.some((c) => c.group === group) ? group : firstCategory(categories);

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomAlarmDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  /** The last create's outcome, in the boat's terms. Survives the selection. */
  const [receipt, setReceipt] = useState<{ title: string; message: string } | null>(null);

  const rows = useMemo(
    () => (selectedGroup === null ? [] : signalsInGroup(signals, selectedGroup)),
    [signals, selectedGroup]
  );
  const selected = selectedPath === null ? undefined : catalogByPath.get(selectedPath);

  function pick(signal: Signal): void {
    setSelectedPath(signal.path);
    setDraft(withAutoTitle(draftForSignal(signal, units), signal));
    setServerError(null);
  }

  function clearSelection(): void {
    setSelectedPath(null);
    setDraft(null);
    setServerError(null);
  }

  /** Every draft edit re-syncs the auto title, so the name follows the sentence. */
  const apply = (next: CustomAlarmDraft): void => {
    setDraft(selected === undefined ? next : withAutoTitle(next, selected));
    setServerError(null);
  };

  const blocked = draft === null || signals.length === 0 ? null : createBlocker(draft, signals);

  async function create(): Promise<void> {
    if (draft === null || busy || blocked !== null) return;
    const body = toCreateBody(draft);
    if (body === null) return;
    setBusy(true);
    setServerError(null);
    try {
      const res = await api<CreateResponse>(`/v1/boats/${boatId}/alerts`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      });
      // Ask for the snapshot before showing the receipt. The refetch is in
      // flight while the receipt renders, so the list underneath catches up a
      // beat later — which is why the receipt claims only what the CLOUD has
      // ("created", and whether the boat is watching it), never that the row is
      // already on screen.
      onCreated();
      setReceipt({
        title: body.title,
        message: proposalCreatedMessage(res.compileStatus),
      });
      clearSelection();
    } catch (err) {
      setServerError(mapCreateError(err, errorContextFor(draft)).message);
    } finally {
      setBusy(false);
    }
  }

  const segments: SentenceSegment[] =
    draft === null || selected === undefined
      ? []
      : sentenceSegments(
          sentenceInputFromDraft(draft, {
            subject: signalDisplayName(selected),
            // The chip beside the BOX, so it names the unit the box is in —
            // `customValueUnits` is the one resolver that decides that, and the
            // number in the box came out of it.
            unit: unitForBox(draft, selected),
            vesselType,
          })
        );
  const watchOptions = draft === null ? null : watchSlotOptions(draft.enabledModes, vesselType);

  return (
    <section className="al-picker" data-testid="alarms-picker" aria-label="Add an alarm">
      <header className="al-picker__head">
        <h3 className="al-picker__title">New alarm · what should the boat watch?</h3>
        <button
          type="button"
          className="al-picker__close"
          aria-label="Close"
          data-testid="alarms-picker-close"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </header>

      {/* The last create, still true after the selection clears. It states what
          the BOAT has, not that a button was pressed: a `pending` alarm is saved
          and is not watching anything yet, and saying so is the whole point. */}
      {receipt !== null ? (
        <p className="al-picker__receipt" role="status" data-testid="alarms-picker-receipt">
          <CheckCircleFillIcon className="al-picker__receipt-icon" />
          <span>
            <strong>{receipt.title}</strong> — {receipt.message}
          </span>
        </p>
      ) : null}

      {categories.length === 0 ? (
        <p className="al-picker__empty">
          This boat has not reported any signals yet, so there is nothing to watch here. Ready-made
          alarms can still be set up in advance.
        </p>
      ) : (
        <div className="al-picker__body">
          <nav className="al-picker__rail" aria-label="Signal categories">
            {categories.map((category) => (
              <button
                key={category.group}
                type="button"
                className={
                  'al-picker__cat' +
                  (category.group === selectedGroup ? ' al-picker__cat--selected' : '')
                }
                aria-pressed={category.group === selectedGroup}
                data-testid={`alarms-picker-cat-${category.group.toLowerCase()}`}
                onClick={() => {
                  setGroup(category.group);
                  clearSelection();
                }}
              >
                <span className="al-picker__cat-name">{category.group}</span>
                <span aria-hidden="true"> · </span>
                <span className="al-picker__cat-count">{category.count}</span>
              </button>
            ))}
          </nav>

          <ul className="al-picker__rows">
            {rows.map((signal) => {
              const row = pickerRow(signal, liveSamples?.get(signal.path), {
                alarmCount: alarmCounts.get(signal.path) ?? 0,
                now,
                units,
              });
              const isSelected = signal.path === selectedPath;
              return (
                <li key={signal.path}>
                  <button
                    type="button"
                    className={
                      'al-picker__row' +
                      (row.freshness.dim ? ' al-picker__row--quiet' : '') +
                      (isSelected ? ' al-picker__row--selected' : '')
                    }
                    aria-pressed={isSelected}
                    data-testid={`alarms-picker-row-${signal.path}`}
                    onClick={() => (isSelected ? clearSelection() : pick(signal))}
                  >
                    <span className="al-picker__name">
                      {row.name}
                      {row.sub !== null ? <span className="al-picker__sub"> {row.sub}</span> : null}
                    </span>
                    <span className="al-picker__value">{row.valueText ?? '—'}</span>
                    <span
                      className={`al-picker__fresh${row.freshness.dim ? ' al-picker__fresh--old' : ''}`}
                    >
                      {row.freshness.text}
                    </span>
                    <span className="al-picker__note">
                      {isSelected ? '‹ selected' : (row.note ?? '')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Step 2. The same slotted sentence the row editor uses, from the same
          segment list — one vocabulary, whether an alarm is being made or
          changed. */}
      {draft !== null && selected !== undefined && watchOptions !== null ? (
        <div className="al-picker__step2">
          <p className="al-sentence" aria-describedby="alarms-picker-said">
            {segments.map((segment, index) => {
              switch (segment.kind) {
                case 'text':
                  return <span key={index}>{segment.text}</span>;
                case 'subject':
                  return <b key={index}>{segment.text}</b>;
                case 'severity':
                  return (
                    <SlotSelect
                      key={index}
                      value={segment.value}
                      options={severityOptions()}
                      onChange={(severity: AlarmSeverity) => apply(setSeverity(draft, severity))}
                      label="Severity"
                      testId="alarms-picker-severity"
                    />
                  );
                case 'number':
                  return (
                    <NumberSlot
                      key={index}
                      value={segment.value}
                      unit={segment.unit}
                      label={segment.label}
                      testId={`alarms-picker-${segment.field}`}
                      placeholder="—"
                      onChange={(value) =>
                        apply(
                          segment.field === 'setpoint'
                            ? setSetpoint(draft, value)
                            : setClearpoint(draft, value)
                        )
                      }
                    />
                  );
                case 'duration':
                  return (
                    <NumberSlot
                      key={index}
                      value={segment.value}
                      unit={DURATION_UNIT}
                      label={segment.label}
                      testId={`alarms-picker-${segment.field}`}
                      onChange={(value) =>
                        apply(
                          segment.field === 'setDelaySeconds'
                            ? setSetDelaySeconds(draft, value)
                            : segment.field === 'clearDelaySeconds'
                              ? setClearDelaySeconds(draft, value)
                              : setStaleSeconds(draft, value)
                        )
                      }
                    />
                  );
                case 'watch':
                  return (
                    <SlotSelect
                      key={index}
                      value={segment.preset}
                      options={watchOptions.options}
                      onChange={(preset: WatchPresetId) => {
                        if (isInertWatchPreset(preset)) return;
                        apply(setEnabledModes(draft, watchPresetModes(preset)));
                      }}
                      label="When to watch"
                      testId="alarms-picker-watch"
                    />
                  );
              }
            })}{' '}
            {/* Conditions need a signal picker of their own, and that is the
                full builder's job — a link, never half of one here. The draft
                travels with it: the chosen signal, threshold, delays and
                severity are exactly what the owner just typed, and a builder
                opening blank would ask for all of it again. */}
            <Link
              className="al-textbtn"
              to={`/dashboard/boats/${boatId}/alarms/new/custom`}
              state={{ customPrefill: draft }}
              data-testid="alarms-picker-conditions"
            >
              + but only while…
            </Link>
          </p>
          <span className="sr-only" id="alarms-picker-said">
            {segmentsPlainText(segments)}
          </span>

          {/* One slot for both kinds of "not yet": what the draft is still
              missing, and what the server said about the last attempt. */}
          {serverError !== null || blocked !== null ? (
            <p className="al-picker__error" role="alert" data-testid="alarms-picker-error">
              {serverError ?? blocked}
            </p>
          ) : null}

          <div className="al-picker__foot">
            <button
              type="button"
              className="al-btn al-btn--primary"
              disabled={busy || blocked !== null}
              data-testid="alarms-picker-create"
              onClick={() => void create()}
            >
              {busy ? 'Creating…' : 'Create on boat'}
            </button>
            <button
              type="button"
              className="al-btn al-btn--secondary"
              data-testid="alarms-picker-cancel"
              onClick={clearSelection}
            >
              Cancel
            </button>
            <p className="al-picker__caption">
              Defaults come from what this signal is · applied when the boat next connects
            </p>
          </div>
          {/* The name the alarm will carry. It is written from the sentence
              above, so it is stated rather than left to be discovered in the
              list afterwards. */}
          <p className="al-picker__named">
            Saved as “{autoTitleFor(draft, selected) ?? draft.title}”
          </p>
        </div>
      ) : null}

      <p className="al-picker__browse">
        <Link
          className="al-textbtn"
          to={`/dashboard/boats/${boatId}/alarms/new`}
          data-testid="alarms-picker-browse"
        >
          Browse ready-made alarms →
        </Link>
      </p>
    </section>
  );
}
